import { type PlanType } from '@/types';

import { bakedAvatarAssetsUrl } from '../../../../consts/env';

/** Figma design token: color/violet/50 */
export const AVATAR_BG_COLOR = '#f3f2ff';

const BAKED_AVATAR_PATH = '/static/assets/baked-avartar-images';

export function buildBakedAvatarCdnBase(
  assetsBaseUrl: string = bakedAvatarAssetsUrl,
) {
  return `${assetsBaseUrl.trim().replace(/\/+$/, '')}${BAKED_AVATAR_PATH}`;
}

export const BAKED_AVATAR_CDN_BASE = buildBakedAvatarCdnBase();

export const SIZE_PIXEL_MAP = {
  xxs: 20,
  xs: 26,
  sm: 30,
  md: 34,
  lg: 36,
} as const;

export type AvatarSize = keyof typeof SIZE_PIXEL_MAP;

export const SIZE_MAP = {
  xxs: 'size-[20px]',
  xs: 'size-[26px]',
  sm: 'size-[30px]',
  md: 'size-[34px]',
  lg: 'size-[36px]',
} as const satisfies Record<AvatarSize, string>;

export const DEFAULT_AVATAR_IMAGE = '/assets/profile_free.webp';

export const PLAN_GRADIENT_CLASS: Record<PlanType, string> = {
  FREE: '',
  BASIC: 'bg-gradient-to-r from-mint-600 to-green-500',
  PRO: 'bg-gradient-to-r from-violet-400 to-blue-400',
};

const BAKED_AVATAR_ACCESSORY_SLOTS = ['glasses', 'mask'] as const;

export function normalizeAvatarPart(
  value: number | null | undefined,
  fallback = 1,
) {
  if (typeof value !== 'number' || !Number.isFinite(value) || value === 0) {
    return fallback;
  }
  return Math.floor(Math.abs(value));
}

function normalizeAccessoryPart(value: number | null | undefined) {
  if (typeof value !== 'number' || !Number.isFinite(value) || value === 0) {
    return 0;
  }
  return Math.floor(Math.abs(value));
}

export function buildBakedAvatarFileName(avatarData: {
  skinId?: number | null;
  clothesId?: number | null;
  faceId?: number | null;
  hairId?: number | null;
  accessoryIds?: Record<string, number | null | undefined> | null;
}) {
  const skin = normalizeAvatarPart(avatarData.skinId);
  const clothes = normalizeAvatarPart(avatarData.clothesId);
  const face = normalizeAvatarPart(avatarData.faceId);
  const hair = normalizeAvatarPart(avatarData.hairId);
  const accessorySlots = BAKED_AVATAR_ACCESSORY_SLOTS.map(slot =>
    normalizeAccessoryPart(avatarData.accessoryIds?.[slot]),
  );

  if (!accessorySlots.some(Boolean)) {
    return `${skin}-${clothes}-${face}-${hair}.png`;
  }

  return `${skin}-${clothes}-${face}-${hair}-${accessorySlots.join('-')}.png`;
}

export function buildBakedAvatarUrl(
  avatarData: {
    skinId?: number | null;
    clothesId?: number | null;
    faceId?: number | null;
    hairId?: number | null;
    accessoryIds?: Record<string, number | null | undefined> | null;
  },
  assetsBaseUrl: string = bakedAvatarAssetsUrl,
): string {
  return `${buildBakedAvatarCdnBase(assetsBaseUrl)}/${buildBakedAvatarFileName(
    avatarData,
  )}`;
}
